import { type DataClass, getDataClass, load } from 'scrivito'

import type {
  AdapterReferenceFilter,
  AdapterSchemaResult,
  DataAccessAdapter,
  DataQueryOpts,
} from '../../core/engine/contracts'
import { decodeFilterQuery } from '../../core/tools/shared/filterQuery'
import type { SavedFilterStructure } from '@/Atomic/Organisms/FilterBuilder/types'
import { resolvePageContext } from '@/Atomic/Widgets/AiChatBot/core/engine/pipeline/pipeline'
import { DataClassName } from '@/Data/const'
import { fetchDefaultValues } from '@/Data/defaultValues'
import { invalidateViaRegistry } from '@/hooks/useInvalidationRegistry'
import { getReferenceDisplayValue } from '@/utils/CRUD/read/reference'
import { getReferenceFilters } from '@/utils/CRUD/read/reference/getReferenceFilters'
import { getSchemaAttributes } from '@/utils/CRUD/read/schema'
import { getOverviewPageLink, getSchemaName } from '@/utils/dataClassMap'
import { getLocalizedTitle } from '@/utils/schemaLocalization'

function buildTransformOpts(
  opts: DataQueryOpts | undefined,
): Record<string, unknown> {
  const t: Record<string, unknown> = {}
  if (opts?.encodedFilterQuery) {
    t.filters = { query: opts.encodedFilterQuery }
  } else if (opts?.filters) {
    t.filters = opts.filters
  }
  if (opts?.search) {
    t.search = opts.search
  }
  if (opts?.order) {
    t.order = opts.order
  }
  if (opts?.unlimited) {
    // The app-wide NO_LIMIT sentinel (TimeTrackingOverviewWidget/const.ts): Scrivito's
    // transform reads an empty-string limit as "no page cap". Typed number-only, so the
    // sentinel stays here in the connector rather than leaking into DataQueryOpts.
    t.limit = ''
  } else if (opts?.limit !== undefined) {
    t.limit = opts.limit
  }
  return t
}

export const scrivitoDataAccessAdapter: DataAccessAdapter = {
  // ── CRUD (name-based) ──────────────────────────────────────────────────────

  async query(dataClass, opts) {
    const dc = getDataClass(dataClass)
    if (!dc) {
      return []
    }
    const t = buildTransformOpts(opts)
    // Merge referenceFilter into filters for reference-scoped searches.
    if (opts?.referenceFilter) {
      const refFilters = getReferenceFilters(
        dataClass,
        opts.referenceFilter as AdapterReferenceFilter,
      )
      if (Object.keys(refFilters).length > 0) {
        t.filters = { ...(t.filters as object | undefined), ...refFilters }
      }
    }
    // warning: Scrivito does not export DataItem publicly; load() returns unknown.
    // The cast is forced by the missing Scrivito type export and mirrors the
    // existing pattern in search_entities.ts / get_related.ts.
    return (await load(() => dc.all().transform(t).take())) as unknown[]
  },

  async count(dataClass, opts) {
    const dc = getDataClass(dataClass)
    if (!dc) {
      return null
    }
    const t = buildTransformOpts(opts)
    return load(() => dc.all().transform(t).count())
  },

  async get(dataClass, id) {
    const dc = getDataClass(dataClass)
    if (!dc) {
      return null
    }
    return load(() => dc.get(id))
  },

  async create(dataClass, attrs) {
    const dc = getDataClass(dataClass)
    if (!dc) {
      throw new Error(`Unknown DataClass: ${dataClass}`)
    }
    // warning: DataClass.create() is typed by Scrivito but not publicly exported.
    // Exercised by slice 1c-ii; stubbed here for interface completeness.
    return load(() =>
      (
        dc as DataClass & { create(a: Record<string, unknown>): unknown }
      ).create(attrs),
    )
  },

  async update(item, attrs) {
    // warning: DataItem is not publicly exported by Scrivito; structural cast required.
    // Exercised by slice 1c-ii; stubbed here for interface completeness.
    const dataItem = item as { update(a: Record<string, unknown>): void }
    await load(() => dataItem.update(attrs))
  },

  // ── CRUD (resolved DataClass passthrough) ──────────────────────────────────

  async queryResolved(dc, opts) {
    // warning: dc arrives as an opaque DataClass from resolveRelation; cast is
    // required because Scrivito does not export DataClass publicly.
    const scrivitodc = dc as DataClass
    const t = buildTransformOpts(opts)
    return (await load(() => scrivitodc.all().transform(t).take())) as unknown[]
  },

  async countResolved(dc, opts) {
    const scrivitodc = dc as DataClass
    const t = buildTransformOpts(opts)
    return load(() => scrivitodc.all().transform(t).count())
  },

  async getResolved(dc, id) {
    const scrivitodc = dc as DataClass
    return load(() => scrivitodc.get(id))
  },

  // ── Schema helpers ─────────────────────────────────────────────────────────

  getSchemaName(dataClass) {
    return getSchemaName(dataClass)
  },

  async getSchema(schemaName) {
    // warning: getSchemaAttributes returns the library's internal schema types;
    // cast to AdapterSchemaResult is safe because the structures are byte-identical
    // (same field names, same shapes) — the adapter types mirror them without
    // introducing a @/ import into the bot's host-neutral core.
    return (await load(() =>
      getSchemaAttributes(schemaName),
    )) as AdapterSchemaResult
  },

  getLocalizedTitle(item, fallback = '') {
    return getLocalizedTitle(item, fallback)
  },

  async resolveReferenceDisplayValues(ids, referenceTo, referenceFilter) {
    // One load() wraps the whole batch — the same single reactive read the manual
    // FilterBuilder does. Omit ids whose reference resolves to no live record (empty
    // name) so callers get present names and detect missing ids from one map.
    return load(() => {
      const map: Record<string, string> = {}
      for (const id of ids) {
        const name = getReferenceDisplayValue(
          id,
          referenceTo,
          // warning: AdapterReferenceFilter has optional booleans; getReferenceDisplayValue
          // expects the required-boolean ReferenceFilter from @/Data/typesV2. PISA values
          // always carry concrete booleans — the optionality is a contracts artefact.
          referenceFilter as
            | { isExternal: boolean; isPerson: boolean }
            | undefined,
        )
        if (name) {
          map[id] = name
        }
      }
      return map
    })
  },

  getOverviewPageLink(dataClass) {
    return getOverviewPageLink(dataClass)
  },

  getDataClassFor(name) {
    return getDataClass(name)
  },

  resolveDataClass(name) {
    return getDataClass(name)
  },

  invalidate(dataClass) {
    // warning: DataClassName is a string enum; the contract accepts a plain string so
    // the host-neutral core never imports @/Data/const. Cast is safe because callers
    // only pass values they validated via requireDataClassName.
    invalidateViaRegistry(dataClass as DataClassName)
  },

  async getDefaultValues(dataClass) {
    // fetchDefaultValues keys on the kebab schema name (POST /default { entityName });
    // callers pass a DataClass name, so resolve it here — the same call the manual
    // create drawer makes.
    return fetchDefaultValues(getSchemaName(dataClass))
  },

  // ── Filter / reference resolution (CRM vocabulary) ──────────────────────────

  async getAppliedFilterStructure() {
    const { urlQuery } = resolvePageContext()
    if (!urlQuery) {
      return undefined
    }
    const filterId = urlQuery.filter
    if (filterId) {
      const dc = getDataClass(DataClassName.FILTER)
      const item = dc
        ? ((await load(() => dc.get(filterId))) as {
            get: (f: string) => unknown
          } | null)
        : null
      if (!item) {
        return undefined
      }
      const q = item.get('query') as { filters?: SavedFilterStructure } | null
      return q?.filters ?? undefined
    }
    return decodeFilterQuery(urlQuery.query) ?? undefined
  },

  async getStoredSegmentFilter(segmentId) {
    const dc = getDataClass(DataClassName.LIST)
    const segment = dc
      ? ((await load(() => dc.get(segmentId))) as {
          get: (f: string) => unknown
        } | null)
      : null
    const storedQuery = segment
      ? (segment.get('query') as Record<string, unknown> | null)
      : null
    return storedQuery?.filters
  },

  resolveReferenceCandidateClasses(referenceTo, referenceFilter) {
    if (referenceTo !== DataClassName.CONTACT) {
      return [referenceTo]
    }
    const { isPerson } = referenceFilter ?? {}
    if (isPerson == null) {
      return [DataClassName.COMPANY, DataClassName.PERSON]
    }
    return isPerson ? [DataClassName.PERSON] : [DataClassName.COMPANY]
  },

  isPolymorphicReferenceBase(name) {
    return name === DataClassName.CONTACT
  },
}
